import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * MetadataManager is a component for managing page-specific metadata
 * @param {Object} props
 * @param {string} props.title - The page title
 * @param {string} props.description - Meta description of the page
 * @param {string} props.keywords - Comma-separated keywords for SEO
 * @param {Object} props.meta - Additional meta tags as key-value pairs
 * @param {string} props.canonicalUrl - Optional canonical URL
 */
const MetadataManager = ({ 
  title = 'MenuPortal | Seamless API Access to Restaurant Data for Developers', 
  description = 'MenuPortal is your go-to API for accessing real-time restaurant data in JSON format. Designed for developers and businesses, MenuPortal delivers fast, reliable, and scalable solutions to power your applications.',
  keywords = 'restaurant data API, JSON restaurant data, developer API, restaurant menu data, scalable API, MenuPortal, real-time data API, restaurant integration tools',
  meta = {},
  canonicalUrl
}) => {
  // Default title suffix
  const titleSuffix = ' | MenuPortal';
  
  // If title doesn't already include the suffix, add it
  const fullTitle = title.includes('MenuPortal') ? title : `${title}${titleSuffix}`;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph meta tags for social sharing */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      
      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      
      {/* Canonical URL if provided */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Any additional meta tags */}
      {Object.entries(meta).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
    </Helmet>
  );
};

export default MetadataManager; 